import { useId } from 'react';
import type { TranslationKey } from '../../core/i18n/translation';
import { useTranslation } from '../../core/hooks/use-translation';

export interface TextFieldProps {
    label: string;
    type: string;
    value: string;
    autoComplete: string;
    inputMode?: 'text' | 'email' | 'tel' | 'numeric';
    /** False makes the box genuinely optional — the browser stops asking for it too. */
    required?: boolean;
    /**
     * Text welded to the input's left edge — the `+7` on a telephone field. It is decoration and
     * never part of the value: the caller holds the digits that follow it and adds the code back
     * when it sends them.
     */
    prefix?: string;
    placeholder?: string;
    maxLength?: number;
    error?: TranslationKey;
    hint?: string;
    onChange: (value: string) => void;
}

/**
 * One labelled box, with the error under it or the hint when there is no error.
 *
 * Shared by the sign-in and registration forms rather than written twice: they sit next to each
 * other in the same flow, and a field that looked slightly different on the second screen would
 * read as a different kind of form.
 *
 * **The message under the box is tied to it by `aria-describedby`.** `aria-invalid` alone says that
 * something is wrong and not what; a customer who cannot see the red line under the field was told
 * their identifier was rejected and nothing more. The optional marker is a word rather than the
 * absence of an asterisk, because "everything unmarked is required" is a convention people have to
 * already know.
 *
 * The prefix carries `aria-hidden`: the label already says what the box is for, and a screen reader
 * announcing `+7` between the label and the value would be reading out the border of the field.
 */
export function TextField({
    label,
    type,
    value,
    autoComplete,
    inputMode,
    required = true,
    prefix,
    placeholder,
    maxLength,
    error,
    hint,
    onChange
}: TextFieldProps) {
    const { t } = useTranslation();
    const id = useId();
    const messageId = `${id}-message`;
    const message = error !== undefined ? t(error) : hint;
    const border =
        error === undefined
            ? 'border-slate-300 dark:border-slate-700'
            : 'border-rose-400 dark:border-rose-600';

    return (
        <div>
            <label
                htmlFor={id}
                className="flex items-baseline gap-2 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
                {label}
                {!required && (
                    <span className="text-xs font-normal text-slate-400 dark:text-slate-500">
                        {t('common.optional')}
                    </span>
                )}
            </label>

            <div
                className={`mt-1.5 flex w-full items-stretch overflow-hidden rounded-xl border bg-white transition focus-within:ring-4 dark:bg-slate-950 ${border} ${
                    error === undefined
                        ? 'focus-within:border-brand-500 focus-within:ring-brand-100 dark:focus-within:border-brand-500 dark:focus-within:ring-brand-950'
                        : 'focus-within:border-rose-500 focus-within:ring-rose-100 dark:focus-within:ring-rose-950'
                }`}
            >
                {prefix !== undefined && (
                    <span
                        aria-hidden="true"
                        className={`flex items-center border-r bg-slate-50 px-3.5 py-2.5 text-sm font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300 ${border}`}
                    >
                        {prefix}
                    </span>
                )}

                <input
                    id={id}
                    className="w-full bg-transparent px-3.5 py-2.5 text-slate-900 outline-none dark:text-slate-100"
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    inputMode={inputMode}
                    maxLength={maxLength}
                    required={required}
                    aria-invalid={error !== undefined}
                    aria-describedby={message === undefined ? undefined : messageId}
                    onChange={(event) => onChange(event.target.value)}
                />
            </div>

            {message !== undefined && (
                <span
                    id={messageId}
                    className={`mt-1.5 block text-sm ${
                        error === undefined
                            ? 'text-slate-500 dark:text-slate-400'
                            : 'font-medium text-rose-600 dark:text-rose-400'
                    }`}
                >
                    {message}
                </span>
            )}
        </div>
    );
}
